/**
 * Profile Settings Section
 * Allows users to update their name, ministry role, and denomination
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import Select from '../common/Select';
import { config } from '../../config';
import { DENOMINATIONS } from '../../constants/denominations';

const MINISTRY_ROLES = [
  { value: 'pastor', label: 'Pastor' },
  { value: 'bishop', label: 'Bishop' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'elder', label: 'Elder' },
  { value: 'deacon', label: 'Deacon' },
  { value: 'minister', label: 'Minister' },
  { value: 'evangelist', label: 'Evangelist' },
  { value: 'first_lady', label: 'First Lady' },
  { value: 'deaconess', label: 'Deaconess' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'student', label: 'Student/Seminary' },
  { value: 'scholar', label: 'Scholar/Academic' },
  { value: 'lay-leader', label: 'Lay Leader' },
  { value: 'other', label: 'Other' },
];

export default function ProfileSettings() {
  const { user } = useAuthStore();
  const { updateSettings, setHasUnsavedChanges } = useSettingsStore();

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [preferredName, setPreferredName] = useState(user?.preferred_name || '');
  const [ministryRole, setMinistryRole] = useState(user?.ministry_role || '');
  const [denomination, setDenomination] = useState(user?.denomination || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Detect changes
  useEffect(() => {
    const hasChanges =
      fullName !== (user?.full_name || '') ||
      preferredName !== (user?.preferred_name || '') ||
      ministryRole !== (user?.ministry_role || '') ||
      denomination !== (user?.denomination || '');

    setHasUnsavedChanges(hasChanges);
  }, [fullName, preferredName, ministryRole, denomination, user, setHasUnsavedChanges]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await updateSettings({
        full_name: fullName,
        preferred_name: preferredName,
        ministry_role: ministryRole,
        denomination: denomination,
      });

      setSaveMessage('Profile updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">Profile Information</h3>
        <p className="text-sm text-dark-text-muted">
          Update your personal information and ministry details.
        </p>
      </div>

      {/* Doctrinal Protection Notice */}
      <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-accent-blue" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-accent-blue mb-2">
              Doctrinal Protection Enabled
            </h4>
            <p className="text-sm text-dark-text-muted leading-relaxed">
              Your selected <strong>denomination</strong> and <strong>ministry role</strong> constrain all Virtual Assistant responses to align with your doctrinal commitments and communication needs. If you request perspectives outside your denomination, you'll receive a clear warning. This ensures PARACLETE respects your theological tradition while providing helpful assistance.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-2">
            Full Name
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-dark-text placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
            placeholder="John Doe"
          />
        </div>

        {/* Preferred Name */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-2">
            Preferred Name
          </label>
          <input
            type="text"
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-dark-text placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
            placeholder="What should PARACLETE call you?"
          />
          <p className="mt-1 text-xs text-dark-text-muted">
            This is how PARACLETE will address you in conversations.
          </p>
        </div>

        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full bg-dark-bg/30 border border-dark-border/50 rounded-xl px-4 py-3 text-dark-text-muted cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-dark-text-muted">
            Email cannot be changed. Contact support if you need to update it.
          </p>
        </div>

        {/* Ministry Role */}
        <Select
          value={ministryRole}
          onChange={setMinistryRole}
          options={MINISTRY_ROLES}
          label="Ministry Role"
          placeholder="Select your role..."
        />

        {/* Denomination */}
        <Select
          value={denomination}
          onChange={setDenomination}
          options={DENOMINATIONS}
          label="Denomination / Church Tradition"
          placeholder="Select your denomination..."
        />
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4 pt-4 border-t border-dark-border/50">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>

        {saveMessage && (
          <span className={`text-sm ${saveMessage.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}
